import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import {
  createFormFieldsByIdGetter,
  createFormValidationFn,
  createInitialValues,
  hideFieldIf,
  scrollToFormField,
} from 'client/helpers/formikHelpers';

import SrCard, {
  SR_CARD_BULLET_COLOR_BLUE,
  SR_CARD_CONTENT_TYPE_FORM_GRID,
} from 'client/components/SrCard/SrCard';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrBox from 'client/components/SrBox';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrGridRow from 'client/components/SrGridRow/SrGridRow';
import SrFooterSaveButton from 'client/components/SrFooterSaveButton';

const getFormFields = ({ values = {} }) => {
  return [
    {
      id: 'earlyLeaseDraftingEnabled',
      title: 'Allow leases to be drafted BEFORE the deposit is successful?',
      type: 'yes_no',
    },
    {
      id: 'firstMonthRentEnabled',
      title: "Ask to collect 1st month's rent?",
      type: 'yes_no',
    },
    {
      id: 'collectRbpFeeEnabled',
      title: 'Ask to collect Resident Benefits Package fee?',
      type: 'yes_no',
    },
    {
      id: 'collectAdminFeeEnabled',
      title: 'Ask to collect admin fee?',
      type: 'yes_no',
    },
    {
      id: 'collectLastMonthRentEnabled',
      title: "Ask to collect last month's rent?",
      type: 'yes_no',
    },
    {
      id: 'collectAdminFee',
      title: 'Collect Admin Fee?',
      type: 'yes_no',
    },
    {
      id: 'leasingInformationEnabled',
      title: 'Collect Lease Info?',
      titleSecondary: '(Enable/disable all lease questions)',
      type: 'yes_no',
    },
    {
      id: 'listingAgreementEnabled',
      title: 'Listing Agreement upload option.',
      type: 'yes_no',
    },
    {
      id: 'propertyOwnerNameEnabled',
      title: 'Business/Landlord Name',
      type: 'yes_no',
    },
    {
      id: 'propertyOwnerEmailEnabled',
      title: 'Business/Landlord Email',
      type: 'yes_no',
    },
    {
      id: 'propertyOwnerPhoneEnabled',
      title: 'Business/Landlord Phone',
      type: 'yes_no',
    },
    {
      id: 'managementEnabled',
      title: 'Is the business the repair contact?/Does the landlord manage the property?',
      type: 'yes_no',
    },
    {
      id: 'managementNameEnabled',
      title: 'Management: Name',
      type: 'yes_no',
    },
    {
      id: 'managementEmailEnabled',
      title: 'Management: Email',
      type: 'yes_no',
    },
    {
      id: 'managementPhoneEnabled',
      title: 'Management: Phone',
      type: 'yes_no',
    },
    {
      id: 'repairContactEnabled',
      title: 'Is the repair contact the same as the management contact?',
      type: 'yes_no',
    },
    {
      id: 'repairContactNameEnabled',
      title: 'Repair contact name',
      type: 'yes_no',
    },
    {
      id: 'repairContactEmailEnabled',
      title: 'Repair contact email',
      type: 'yes_no',
    },
    {
      id: 'repairContactPhoneEnabled',
      title: 'Repair contact phone',
      type: 'yes_no',
    },
    {
      id: 'whoCollectsRentPaymentEnabled',
      title: 'Who will collect rent payments?',
      type: 'yes_no',
    },
    {
      id: 'rentPaymentDescriptionEnabled',
      title: 'How will the renter pay rent?',
      type: 'yes_no',
    },
    {
      id: 'additionalAddendaEnabled',
      title: 'Addenda to Include',
      type: 'yes_no',
    },
    {
      id: 'partnerLeaseNotesEnabled',
      title: 'Extra notes',
      type: 'yes_no',
    },
    {
      id: 'billingPhone',
      title: 'Billing Phone',
      type: 'phone',
      validations: ['phone'],
    },
    {
      id: 'billingEmail',
      title: 'Billing Email',
      type: 'email',
      validations: ['email'],
    },
    {
      id: 'sendApprovedAppInfoTo3rdParty',
      title: 'Send Approved Applicant contact info to 3rd-party email?',
      type: 'yes_no',
      validations: ['required'],
    },
    hideFieldIf(!values.sendApprovedAppInfoTo3rdParty, {
      id: 'approvedAppInfo3rdPartyEmail',
      title: '3rd-party email address',
      type: 'email',
      validations: ['email', 'required'],
    }),
  ];
};

const SrPartnerApprovalFlowSettingsForm = ({ partner, onSubmit, inProgress }) => {
  const location = useLocation();

  useEffect(() => {
    if (location.search.includes('scrollTo=billing')) {
      scrollToFormField({
        formFieldId: 'billingPhone',
      });
    }
  });

  const _getDefaultValues = () => {
    return {
      ...partner.experienceConfiguration,
      billingEmail: partner.billingEmail,
      billingPhone: partner.billingPhone,
    };
  };

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={createInitialValues({
        formFields: getFormFields({
          values: _getDefaultValues(),
        }),
        defaultValues: _getDefaultValues(),
      })}
      validate={(values) => {
        const formFields = getFormFields({ values });
        return createFormValidationFn(formFields)(values);
      }}
      render={(formikBag) => {
        const formFields = getFormFields({ values: formikBag.values });
        const getFormFieldsById = createFormFieldsByIdGetter(formFields);

        return (
          <>
            <Form>
              <SrCard
                color={SR_CARD_BULLET_COLOR_BLUE}
                contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                header="Lease Drafting"
              >
                <SrGridRow>
                  <SrFormikField
                    formField={getFormFieldsById('earlyLeaseDraftingEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrBox height="50px" />
                  <SrFormikField
                    formField={getFormFieldsById('firstMonthRentEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrFormikField
                    formField={getFormFieldsById('collectAdminFee')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrFormikField
                    formField={getFormFieldsById('collectRbpFeeEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrFormikField
                    formField={getFormFieldsById('collectAdminFeeEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrFormikField
                    formField={getFormFieldsById('collectLastMonthRentEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrBox height="50px" />
                  <SrFormikField
                    formField={getFormFieldsById('leasingInformationEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrBox height="50px" />
                  <SrFormikField
                    formField={getFormFieldsById('listingAgreementEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrBox height="50px" />
                  <SrFormikField
                    formField={getFormFieldsById('propertyOwnerNameEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrFormikField
                    formField={getFormFieldsById('propertyOwnerEmailEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrFormikField
                    formField={getFormFieldsById('propertyOwnerPhoneEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrFormikField
                    formField={getFormFieldsById('managementEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrFormikField
                    formField={getFormFieldsById('managementNameEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrFormikField
                    formField={getFormFieldsById('managementEmailEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrFormikField
                    formField={getFormFieldsById('managementPhoneEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrFormikField
                    formField={getFormFieldsById('repairContactEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrFormikField
                    formField={getFormFieldsById('repairContactNameEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrFormikField
                    formField={getFormFieldsById('repairContactEmailEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrFormikField
                    formField={getFormFieldsById('repairContactPhoneEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrBox height="50px" />
                  <SrFormikField
                    formField={getFormFieldsById('whoCollectsRentPaymentEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrFormikField
                    formField={getFormFieldsById('rentPaymentDescriptionEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrBox height="50px" />
                  <SrFormikField
                    formField={getFormFieldsById('additionalAddendaEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrBox height="50px" />
                  <SrFormikField
                    formField={getFormFieldsById('partnerLeaseNotesEnabled')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                </SrGridRow>
              </SrCard>
              <SrCard
                color={SR_CARD_BULLET_COLOR_BLUE}
                contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                header="Notifications"
              >
                <SrGridRow>
                  <SrFormikField
                    formField={getFormFieldsById('sendApprovedAppInfoTo3rdParty')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrFormikField
                    formField={getFormFieldsById('approvedAppInfo3rdPartyEmail')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                </SrGridRow>
              </SrCard>
              <SrCard
                color={SR_CARD_BULLET_COLOR_BLUE}
                contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                header="Billing"
              >
                <SrGridRow>
                  <SrFormikField
                    formField={getFormFieldsById('billingPhone')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                  <SrFormikField
                    formField={getFormFieldsById('billingEmail')}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                  />
                </SrGridRow>
              </SrCard>

              <SrStaticFooter>
                <span />
                <SrFooterSaveButton isSubmitting={inProgress} />
              </SrStaticFooter>
            </Form>
          </>
        );
      }}
    />
  );
};

SrPartnerApprovalFlowSettingsForm.propTypes = {
  partner: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  inProgress: PropTypes.bool.isRequired,
};

SrPartnerApprovalFlowSettingsForm.displayName = 'SrPartnerApprovalFlowSettingsForm';

export default SrPartnerApprovalFlowSettingsForm;
